import { isEnglishLocale } from './locale';

const DEFAULT_CURRENCY_CODE = 'MXN';
const STARTING_AT_REGEX = /^\s*(desde|starting at)\s+/i;
const CURRENCY_CODE_REGEX = /\b(?:mxn|usd)\b/gi;
const DOLLAR_SIGN_REGEX = /\$/g;
const FIRST_NUMBER_REGEX = /-?\d[\d.,\s]*/;
const HAS_DOLLAR_SIGN_REGEX = /\$/;
const PRICE_KEYWORDS = ['precio', 'price'];

const normalizeWhitespace = (value = '') => String(value ?? '').replace(/\s+/g, ' ').trim();

const getFormatterLocale = (locale, currencyCode) => {
  if (isEnglishLocale(locale)) return 'en-US';
  if (currencyCode === 'USD') return 'en-US';
  return 'es-MX';
};

const stripCurrencyMarkers = (value = '') => normalizeWhitespace(
  String(value ?? '')
    .replace(STARTING_AT_REGEX, '')
    .replace(CURRENCY_CODE_REGEX, '')
    .replace(DOLLAR_SIGN_REGEX, '')
);

const normalizeNumericString = (value = '') => {
  const raw = stripCurrencyMarkers(value)
    .replace(/\s+/g, '')
    .replace(/[^\d,.-]/g, '');

  if (!raw) return '';

  if (raw.includes(',') && raw.includes('.')) {
    const lastCommaIndex = raw.lastIndexOf(',');
    const lastDotIndex = raw.lastIndexOf('.');

    if (lastCommaIndex > lastDotIndex) {
      return raw.replace(/\./g, '').replace(',', '.');
    }

    return raw.replace(/,/g, '');
  }

  if (raw.includes(',')) {
    const [, decimalPart = ''] = raw.split(',');
    if (decimalPart.length > 0 && decimalPart.length <= 2) {
      return raw.replace(',', '.');
    }

    return raw.replace(/,/g, '');
  }

  return raw;
};

export const getCurrencySettings = (companyInfo, locale) => {
  const currencyCode = companyInfo?.currencyCode === 'USD' ? 'USD' : DEFAULT_CURRENCY_CODE;

  return {
    currencyCode,
    showCurrency: companyInfo?.showCurrency !== false,
    locale: getFormatterLocale(locale, currencyCode),
  };
};

export const isPriceKey = (value = '') => {
  const normalized = normalizeWhitespace(value).toLowerCase();
  return PRICE_KEYWORDS.some((keyword) => normalized.includes(keyword));
};

export const toNumericPriceValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const normalized = normalizeNumericString(value);
  if (!normalized) return null;

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getLowestPriceFromItems = (items = []) => {
  const prices = items
    .flatMap((item) => item?.features || [])
    .filter((feature) => isPriceKey(feature?.atributo))
    .map((feature) => toNumericPriceValue(feature?.valor))
    .filter((value) => Number.isFinite(value) && value > 0);

  return prices.length > 0 ? Math.min(...prices) : 0;
};

export const getLowestPriceTextFromItems = (items = []) => {
  const prices = items
    .flatMap((item) => item?.features || [])
    .filter((feature) => isPriceKey(feature?.atributo))
    .map((feature) => ({
      text: feature?.valor,
      value: toNumericPriceValue(feature?.valor),
    }))
    .filter(({ value }) => Number.isFinite(value) && value > 0);

  if (!prices.length) return null;

  return prices.reduce((lowest, current) => (
    current.value < lowest.value ? current : lowest
  )).text;
};

export const getStartingAtLabel = (locale) => (
  isEnglishLocale(locale) ? 'Starting at' : 'Desde'
);

export const getComingSoonLabel = (locale) => (
  isEnglishLocale(locale) ? 'Coming Soon' : 'Próximamente'
);

export const formatNumericPrice = (value) => {
  const numericValue = toNumericPriceValue(value);
  if (!Number.isFinite(numericValue)) return null;
  return formatPriceText(String(value));
};

const normalizePriceText = (value) => {
  let text = normalizeWhitespace(String(value ?? '').replace(CURRENCY_CODE_REGEX, ''));
  if (!text) return null;

  text = normalizeWhitespace(text.replace(/\$\s+/g, '$'));

  if (FIRST_NUMBER_REGEX.test(text) && !HAS_DOLLAR_SIGN_REGEX.test(text)) {
    text = normalizeWhitespace(
      text.replace(FIRST_NUMBER_REGEX, (match) => `$${match.trim()}`)
    );
  }

  return normalizeWhitespace(text.replace(/\$\s+/g, '$'));
};

export const formatPriceText = (value, companyInfo) => {
  if (value == null) return null;

  const text = normalizePriceText(value);
  if (!text) return null;

  const hasPriceValue = FIRST_NUMBER_REGEX.test(text) || HAS_DOLLAR_SIGN_REGEX.test(text);
  if (!hasPriceValue) return text;

  const { currencyCode, showCurrency } = getCurrencySettings(companyInfo);
  if (!showCurrency) return text;

  return `${text} ${currencyCode}`;
};

export const formatPriceWithPrefix = (value, companyInfo, locale, prefix = getStartingAtLabel(locale)) => {
  const formatted = formatPriceText(value, companyInfo, locale);
  if (!formatted) return null;

  const cleanPrefix = normalizeWhitespace(prefix);
  if (STARTING_AT_REGEX.test(formatted)) return formatted;

  return cleanPrefix ? `${cleanPrefix} ${formatted}` : formatted;
};
